import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Users,
    UserPlus,
    Search,
    Filter,
    Mail,
    Phone,
    Calendar,
    Shield,
    Star,
    MoreVertical,
    Edit,
    Trash2,
    MessageSquare,
    Eye,
    ChevronRight,
    Download,
    Upload,
    Award,
    Target,
    Clock,
    CheckCircle
} from "lucide-react";
import BackButton from "../../components/BackButton";
import teamImg from "../../imgs/teams.svg";

const TeamManagement = () => {
    const [teamMembers, setTeamMembers] = useState([
        {
            id: 1,
            name: "Alex Johnson",
            email: "alex.johnson@teamhub.com",
            role: "Lead Designer",
            department: "Design",
            status: "active",
            joined: "2023-03-15",
            tasks: { completed: 42, total: 48 },
            performance: 4.8,
            skills: ["UI/UX", "Figma", "Prototyping"]
        },
        {
            id: 2,
            name: "Maria Garcia",
            email: "maria.garcia@teamhub.com",
            role: "Senior Developer",
            department: "Engineering",
            status: "active",
            joined: "2022-11-08",
            tasks: { completed: 65, total: 68 },
            performance: 4.9,
            skills: ["React", "Node.js", "TypeScript"]
        },
        {
            id: 3,
            name: "David Kim",
            email: "david.kim@teamhub.com",
            role: "Project Manager",
            department: "Management",
            status: "active",
            joined: "2023-01-22",
            tasks: { completed: 28, total: 30 },
            performance: 4.7,
            skills: ["Agile", "Scrum", "Jira"]
        },
        {
            id: 4,
            name: "Sarah Wilson",
            email: "sarah.wilson@teamhub.com",
            role: "QA Engineer",
            department: "Quality Assurance",
            status: "on-leave",
            joined: "2023-06-10",
            tasks: { completed: 38, total: 45 },
            performance: 4.5,
            skills: ["Testing", "Automation", "Cypress"]
        },
        {
            id: 5,
            name: "James Miller",
            email: "james.miller@teamhub.com",
            role: "DevOps Engineer",
            department: "Operations",
            status: "active",
            joined: "2023-02-28",
            tasks: { completed: 52, total: 55 },
            performance: 4.6,
            skills: ["Docker", "AWS", "Kubernetes"]
        },
        {
            id: 6,
            name: "Lisa Chen",
            email: "lisa.chen@teamhub.com",
            role: "Data Analyst",
            department: "Analytics",
            status: "inactive",
            joined: "2022-09-14",
            tasks: { completed: 31, total: 40 },
            performance: 4.3,
            skills: ["Python", "SQL", "Tableau"]
        }
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDepartment, setSelectedDepartment] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [newMember, setNewMember] = useState({
        name: "",
        email: "",
        role: "",
        department: ""
    });
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingMember, setEditingMember] = useState(null);

    const departments = ["all", "Design", "Engineering", "Management", "Quality Assurance", "Operations", "Analytics"];
    const statusOptions = ["all", "active", "on-leave", "inactive"];
    const roles = ["Lead Designer", "Senior Developer", "Project Manager", "QA Engineer", "DevOps Engineer", "Data Analyst"];

    const getStatusColor = (status) => {
        switch (status) {
            case "active": return "bg-green-100 text-green-800";
            case "on-leave": return "bg-yellow-100 text-yellow-800";
            case "inactive": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getDepartmentColor = (department) => {
        switch (department) {
            case "Design": return "bg-purple-100 text-purple-800";
            case "Engineering": return "bg-blue-100 text-blue-800";
            case "Management": return "bg-orange-100 text-orange-800";
            case "Quality Assurance": return "bg-green-100 text-green-800";
            case "Operations": return "bg-red-100 text-red-800";
            case "Analytics": return "bg-indigo-100 text-indigo-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const addMember = () => {
        if (newMember.name && newMember.email && newMember.role && newMember.department) {
            const member = {
                id: teamMembers.length + 1,
                name: newMember.name,
                email: newMember.email,
                role: newMember.role,
                department: newMember.department,
                status: "active",
                joined: new Date().toISOString().split('T')[0],
                tasks: { completed: 0, total: 0 },
                performance: 0,
                skills: []
            };
            setTeamMembers([...teamMembers, member]);
            setNewMember({ name: "", email: "", role: "", department: "" });
            setShowAddForm(false);
        }
    };

    const updateMember = () => {
        if (editingMember && newMember.name && newMember.email && newMember.role && newMember.department) {
            setTeamMembers(teamMembers.map(m =>
                m.id === editingMember.id
                    ? { ...m, ...newMember }
                    : m
            ));
            setNewMember({ name: "", email: "", role: "", department: "" });
            setEditingMember(null);
        }
    };

    const deleteMember = (id) => {
        if (window.confirm("Are you sure you want to remove this team member?")) {
            setTeamMembers(teamMembers.filter(m => m.id !== id));
        }
    };

    const filteredMembers = teamMembers.filter(member => {
        const matchesSearch =
            member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.role.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesDepartment = selectedDepartment === "all" || member.department === selectedDepartment;
        const matchesStatus = selectedStatus === "all" || member.status === selectedStatus;

        return matchesSearch && matchesDepartment && matchesStatus;
    });

    const stats = {
        total: teamMembers.length,
        active: teamMembers.filter(m => m.status === "active").length,
        onLeave: teamMembers.filter(m => m.status === "on-leave").length,
        avgPerformance: (teamMembers.reduce((sum, m) => sum + m.performance, 0) / teamMembers.length).toFixed(1),
        totalTasks: teamMembers.reduce((sum, m) => sum + m.tasks.total, 0),
        completedTasks: teamMembers.reduce((sum, m) => sum + m.tasks.completed, 0)
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <BackButton to="/project-leader/dashboard" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
                                <p className="text-gray-600 mt-1">Manage your team members and their roles</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button className="flex items-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                <Download className="w-4 h-4 mr-2" />
                                Export
                            </button>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setShowAddForm(true)}
                                className="btn-primary flex items-center"
                            >
                                <UserPlus className="w-5 h-5 mr-2" />
                                Add Member
                            </motion.button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-8">
                {/* Stats Overview */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Members</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                                </div>
                                <Users className="w-8 h-8 text-primary-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Active</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                                </div>
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Avg. Performance</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.avgPerformance}</p>
                                </div>
                                <Star className="w-8 h-8 text-yellow-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Tasks Completed</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.completedTasks}</p>
                                </div>
                                <Target className="w-8 h-8 text-blue-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">On Leave</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.onLeave}</p>
                                </div>
                                <Clock className="w-8 h-8 text-orange-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Completion Rate</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {Math.round((stats.completedTasks / stats.totalTasks) * 100)}%
                                    </p>
                                </div>
                                <Award className="w-8 h-8 text-purple-600" />
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Search and Filters */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search team members..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>
                        <div className="flex gap-3">
                            <select
                                value={selectedDepartment}
                                onChange={(e) => setSelectedDepartment(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>
                                        {dept === "all" ? "All Departments" : dept}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {statusOptions.map(status => (
                                    <option key={status} value={status}>
                                        {status === "all" ? "All Status" : status.replace('-', ' ')}
                                    </option>
                                ))}
                            </select>
                            <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                                <Filter className="w-5 h-5 mr-2" />
                                More Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Add/Edit Form */}
                {(showAddForm || editingMember) && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card mb-8"
                    >
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">
                            {editingMember ? "Edit Team Member" : "Add New Team Member"}
                        </h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    value={newMember.name}
                                    placeholder="Enter full name"
                                    onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    value={newMember.email}
                                    placeholder="Enter email address"
                                    onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Role *
                                </label>
                                <select
                                    value={newMember.role}
                                    onChange={(e) => setNewMember({...newMember, role: e.target.value})}
                                    className="input-field"
                                    required
                                >
                                    <option value="">Select role</option>
                                    {roles.map(role => (
                                        <option key={role} value={role}>{role}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Department *
                                </label>
                                <select
                                    value={newMember.department}
                                    onChange={(e) => setNewMember({...newMember, department: e.target.value})}
                                    className="input-field"
                                    required
                                >
                                    <option value="">Select department</option>
                                    {departments.filter(d => d !== "all").map(dept => (
                                        <option key={dept} value={dept}>{dept}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddForm(false);
                                    setEditingMember(null);
                                    setNewMember({ name: "", email: "", role: "", department: "" });
                                }}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingMember ? updateMember : addMember}
                                className="btn-primary"
                            >
                                {editingMember ? "Update Member" : "Add Member"}
                            </button>
                        </div>
                    </motion.div>
                )}

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Team Members List */}
                    <div className="lg:col-span-2">
                        <div className="space-y-4">
                            {filteredMembers.map((member, index) => (
                                <motion.div
                                    key={member.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="card hover:shadow-medium group"
                                >
                                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-start space-x-3">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                            <span className="text-white font-semibold text-lg">
                              {member.name.split(' ').map(n => n[0]).join('')}
                            </span>
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center space-x-3 mb-1">
                                                            <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                                                            <div className="flex items-center space-x-2">
                                <span className={`badge ${getStatusColor(member.status)}`}>
                                  {member.status.replace('-', ' ')}
                                </span>
                                                                <span className={`badge ${getDepartmentColor(member.department)}`}>
                                  {member.department}
                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-wrap gap-3 text-sm mb-3">
                                                            <div className="flex items-center space-x-1">
                                                                <Mail className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{member.email}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-1">
                                                                <Shield className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{member.role}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-1">
                                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">Joined: {member.joined}</span>
                                                            </div>
                                                        </div>

                                                        {/* Skills */}
                                                        <div className="flex flex-wrap gap-2 mb-3">
                                                            {member.skills.map((skill, idx) => (
                                                                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                  {skill}
                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingMember(member);
                                                            setNewMember({
                                                                name: member.name,
                                                                email: member.email,
                                                                role: member.role,
                                                                department: member.department
                                                            });
                                                        }}
                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Edit member"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteMember(member.id)}
                                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Remove member"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                    <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                                        <MoreVertical className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Performance and Tasks */}
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center space-x-4">
                                                        <div className="flex items-center space-x-1">
                                                            <Star className="w-4 h-4 text-yellow-500" />
                                                            <span className="text-sm font-medium text-gray-900">{member.performance}/5</span>
                                                        </div>
                                                        <div className="text-sm">
                                                            <span className="text-gray-600">Tasks: </span>
                                                            <span className="font-medium text-gray-900">
                                {member.tasks.completed}/{member.tasks.total}
                              </span>
                                                        </div>
                                                        <div className="text-sm">
                                                            <span className="text-gray-600">Completion: </span>
                                                            <span className="font-medium text-gray-900">
                                {Math.round((member.tasks.completed / member.tasks.total) * 100)}%
                              </span>
                                                        </div>
                                                    </div>
                                                    <button className="flex items-center text-primary-600 hover:text-primary-700 font-medium">
                                                        <MessageSquare className="w-4 h-4 mr-1" />
                                                        Message
                                                    </button>
                                                </div>

                                                <div className="space-y-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">Task Progress</span>
                                                        <span className="font-medium text-gray-900">
                              {Math.round((member.tasks.completed / member.tasks.total) * 100)}%
                            </span>
                                                    </div>
                                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${
                                                                (member.tasks.completed / member.tasks.total) < 0.5 ? 'bg-red-500' :
                                                                    (member.tasks.completed / member.tasks.total) < 0.8 ? 'bg-yellow-500' : 'bg-green-500'
                                                            }`}
                                                            style={{ width: `${(member.tasks.completed / member.tasks.total) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Empty State */}
                        {filteredMembers.length === 0 && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-12"
                            >
                                <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <Users className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No team members found</h3>
                                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                                    {searchTerm || selectedDepartment !== 'all' || selectedStatus !== 'all'
                                        ? "No team members match your search criteria. Try adjusting your filters."
                                        : "Get started by adding your first team member."
                                    }
                                </p>
                                {(searchTerm || selectedDepartment !== 'all' || selectedStatus !== 'all') ? (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setSelectedDepartment('all');
                                            setSelectedStatus('all');
                                        }}
                                        className="btn-primary"
                                    >
                                        Clear Filters
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setShowAddForm(true)}
                                        className="btn-primary"
                                    >
                                        <UserPlus className="w-5 h-5 mr-2" />
                                        Add Your First Member
                                    </button>
                                )}
                            </motion.div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Team Overview */}
                        <div className="card">
                            <div className="text-center mb-6">
                                <img
                                    src={teamImg}
                                    alt="Team Management"
                                    className="w-full max-w-xs mx-auto mb-4 rounded-lg"
                                />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Team Overview</h3>
                                <p className="text-gray-600 text-sm">
                                    Manage team members, assign roles, and track performance metrics.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <Users className="w-5 h-5 text-blue-600" />
                                        <span className="font-medium text-gray-700">Team Size</span>
                                    </div>
                                    <span className="font-medium text-gray-900">{stats.total} members</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <Star className="w-5 h-5 text-yellow-600" />
                                        <span className="font-medium text-gray-700">Avg. Performance</span>
                                    </div>
                                    <span className="font-medium text-gray-900">{stats.avgPerformance}/5</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <Target className="w-5 h-5 text-green-600" />
                                        <span className="font-medium text-gray-700">Task Completion</span>
                                    </div>
                                    <span className="font-medium text-gray-900">
                    {Math.round((stats.completedTasks / stats.totalTasks) * 100)}%
                  </span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                            <div className="space-y-2">
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <MessageSquare className="w-5 h-5" />
                                        <span className="font-medium">Team Announcement</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Upload className="w-5 h-5" />
                                        <span className="font-medium">Import Members</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Eye className="w-5 h-5" />
                                        <span className="font-medium">View Reports</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                            </div>
                        </div>

                        {/* Department Breakdown */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Breakdown</h3>
                            <div className="space-y-3">
                                {departments.filter(d => d !== "all").map(dept => {
                                    const count = teamMembers.filter(m => m.department === dept).length;
                                    const percentage = Math.round((count / stats.total) * 100);

                                    return (
                                        <div key={dept} className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <div className={`w-3 h-3 rounded-full ${getDepartmentColor(dept).split(' ')[0]}`} />
                                                    <span className="text-sm text-gray-600">{dept}</span>
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">
                          {count} ({percentage}%)
                        </span>
                                            </div>
                                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${getDepartmentColor(dept).split(' ')[0]} rounded-full`}
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamManagement;